import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { formatDate } from './cycleStatus'
import { fmtNum, humanizeCode } from './healthReportFormat'
import { markerValueText, summaryHasItems } from './supplementaryFormat'

function Group({ title, items, render, testId }) {
  if (!items?.length) return null
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold uppercase text-gray-600">{title}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-gray-800">
        {items.map((item, i) => <li key={i}>{render(item)}</li>)}
      </ul>
    </div>
  )
}

// VH-233: "What changed because of the supplementary test" — a revision against the previous
// finalized version: findings, target conditions, recommendations and next tests.
export function RevisionChangeSummary({ cycleId, versionNumber, enabled = true }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { data, isLoading } = useQuery({
    queryKey: ['healthCycleChangeSummary', cycleId, versionNumber],
    queryFn: () => healthCyclesAPI.getChangeSummary(cycleId, versionNumber),
    select: (response) => response.data,
    enabled: enabled && !!cycleId && !!versionNumber,
  })
  if (isLoading) return <p className="text-sm text-gray-500">{t('healthCycle.changeSummary.loading')}</p>
  if (!data || data.base_version_number == null) return null

  const tests = Object.fromEntries((data.supplementary_tests?.used ?? []).concat(data.supplementary_tests?.detached ?? [])
    .map((x) => [x.blood_test_id, x]))
  const bySupplementary = (data.supplementary_tests?.attached?.length ?? 0) > 0 || (data.supplementary_tests?.detached?.length ?? 0) > 0
  // Only a change the supplementary value produced is attributed to it; anything else in the same
  // revision (e.g. a score recomputed from stale stored data) is labelled as a recalculation.
  const caused = (id) => {
    if (id && tests[id]) return ` — ${t('healthCycle.changeSummary.causedBy', { date: formatDate(tests[id].test_date, lang) })}`
    return bySupplementary ? ` — ${t('healthCycle.changeSummary.recalculated')}` : ''
  }
  const target = (key) => t(`cycleReports.targetKey.${key}`, { defaultValue: humanizeCode(key) })
  const menuWeeks = data.menu?.weeks ?? []
  const menuItem = (x) => ({
    name: x?.name ?? '', grams: x?.quantity_g == null ? '' : Math.round(Number(x.quantity_g)),
    slot: x?.meal_slot ? t(`mealPrep.slots.${x.meal_slot}`, x.meal_slot) : '',
    days: (x?.day_types ?? []).map((d) => t(`mealPrep.dayTypes.${d}`, d)).join(' / '),
  })
  const n = (v) => (v == null ? '—' : fmtNum(v, lang))

  const finding = (f) => {
    const name = f.marker_name ?? humanizeCode(f.marker_code)
    if (f.reason === 'settled') {
      const by = f.settled_by ?? {}
      return `${name}: ${t('healthCycle.changeSummary.reason.settled', { marker: by.marker_name ?? '', value: markerValueText(by, lang) })}${caused(f.caused_by)}`
    }
    if (f.reason) {
      return `${name}: ${t(`healthCycle.changeSummary.reason.${f.reason}`, { value: markerValueText(f.after, lang) })}${caused(f.caused_by)}`
    }
    const values = f.before ? `${markerValueText(f.before, lang) || '—'} → ${markerValueText(f.after, lang)}` : markerValueText(f.after, lang)
    return `${name}: ${values}${f.after?.classification ? ` (${t(`cycleReports.classification.${f.after.classification}`, { defaultValue: humanizeCode(f.after.classification) })})` : ''}${caused(f.caused_by)}`
  }
  const condition = (c) => t('healthCycle.changeSummary.conditionResolved', {
    target: target(c.target),
    condition: t(`cycleReports.condition.${c.code}`, {
      test: t(`cycleReports.resolvingTest.${c.condition?.resolving_test}`, { defaultValue: humanizeCode(c.condition?.resolving_test) }),
      marker: c.condition?.marker ?? '', value: n(c.condition?.value), unlogged: n(c.condition?.unlogged_kcal_per_day),
      defaultValue: humanizeCode(c.code),
    }),
  }) + caused(c.caused_by)
  const targetLine = (x) => `${t('healthCycle.changeSummary.targetLine', {
    target: target(x.target), before: `${n(x.before.computed_value)} ${x.unit ?? ''}`.trim(), after: `${n(x.after.computed_value)} ${x.unit ?? ''}`.trim(),
  })}${x.user_set ? ` · ${t('healthCycle.changeSummary.targetUserSet', { value: `${n(x.after.value)} ${x.unit ?? ''}`.trim() })}` : ''}`

  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3" data-testid="revision-change-summary">
      <div>
        <p className="text-sm font-semibold text-gray-900">
          {bySupplementary ? t('healthCycle.changeSummary.title') : t('healthCycle.changeSummary.titleGeneric')}
        </p>
        <p className="text-xs text-gray-500">{t('healthCycle.changeSummary.against', { number: data.base_version_number })}</p>
      </div>
      {(data.supplementary_tests?.attached ?? []).map((x) => (
        <p key={`a-${x.blood_test_id}`} className="text-sm text-gray-700">{t('healthCycle.changeSummary.attached', { date: formatDate(x.test_date, lang) })}</p>
      ))}
      {(data.supplementary_tests?.detached ?? []).map((x) => (
        <p key={`d-${x.blood_test_id}`} className="text-sm text-gray-700">{t('healthCycle.changeSummary.detached', { date: formatDate(x.test_date, lang) })}</p>
      ))}
      {!summaryHasItems(data) && <p className="text-sm text-gray-600">{t('healthCycle.changeSummary.none')}</p>}
      <Group testId="summary-findings-resolved" title={t('healthCycle.changeSummary.findingsResolved')} items={data.findings.resolved} render={finding} />
      <Group testId="summary-findings-added" title={t('healthCycle.changeSummary.findingsAdded')} items={data.findings.added} render={finding} />
      <Group title={t('healthCycle.changeSummary.findingsChanged')} items={data.findings.changed} render={finding} />
      <Group testId="summary-conditions-resolved" title={t('healthCycle.changeSummary.conditionsResolved')} items={data.conditions.resolved} render={condition} />
      <Group title={t('healthCycle.changeSummary.conditionsAdded')} items={data.conditions.added}
        render={(c) => `${target(c.target)}: ${t(`cycleReports.condition.${c.code}`, { test: t(`cycleReports.resolvingTest.${c.condition?.resolving_test}`, { defaultValue: humanizeCode(c.condition?.resolving_test) }), marker: c.condition?.marker ?? '', value: n(c.condition?.value), unlogged: n(c.condition?.unlogged_kcal_per_day), defaultValue: humanizeCode(c.code) })}`} />
      <Group title={t('healthCycle.changeSummary.targetsChanged')} items={data.conditions.targets} render={targetLine} />
      <Group title={t('healthCycle.changeSummary.recommendationsAdded')} items={data.recommendations.added} render={(r) => r.title} />
      <Group testId="summary-recommendations-removed" title={t('healthCycle.changeSummary.recommendationsRemoved')} items={data.recommendations.removed} render={(r) => r.title} />
      <Group testId="summary-tests-added" title={t('healthCycle.changeSummary.testsAdded')} items={data.next_tests.added} render={(x) => x.test} />
      <Group testId="summary-tests-removed" title={t('healthCycle.changeSummary.testsRemoved')} items={data.next_tests.removed} render={(x) => x.test} />
      {/* VH-236 follow-up: weekly-menu changes. They are not frozen into the baseline, so the payload
          compare cannot see them — without this a revision that moved the menu read as "no changes". */}
      {menuWeeks.length > 0 && (
        <div data-testid="summary-menu">
          <p className="text-xs font-semibold uppercase text-gray-600">{t('healthCycle.changeSummary.menuChanged')}</p>
          {menuWeeks.map((w) => (
            <div key={w.week_start ?? 'week'} className="mt-1">
              <p className="text-sm font-medium text-gray-800">
                {t('healthCycle.changeSummary.menuWeek', {
                  start: formatDate(w.week_start, lang), end: formatDate(w.week_end, lang),
                })}
              </p>
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-gray-800">
                {(w.added ?? []).map((x, i) => <li key={`a${i}`}>{t('healthCycle.changeSummary.menuAdded', menuItem(x))}</li>)}
                {(w.removed ?? []).map((x, i) => <li key={`r${i}`}>{t('healthCycle.changeSummary.menuRemoved', menuItem(x))}</li>)}
                {(w.replaced ?? []).map((x, i) => (
                  <li key={`p${i}`}>
                    {t('healthCycle.changeSummary.menuReplaced', { ...menuItem(x.after), before: x.before?.name ?? '' })}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="mt-1 text-xs text-gray-500">{t('healthCycle.changeSummary.menuNotFrozen')}</p>
        </div>
      )}
    </div>
  )
}

export default RevisionChangeSummary
